import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, ShoppingBag } from "lucide-react";
import Footer from "@/components/Footer";
import { BackLink } from "@/components/PageHeader";
import AccountMenu, { SignupLink } from "@/components/AccountMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gallery() {
  const [collections, setCollections] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((r) => setCollections(r.data))
      .catch(() => setCollections([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-[#FBF9F5]/75 border-b border-[#0D1117]/10">
        <nav className="px-6 sm:px-12 lg:px-20 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" data-testid="gallery-brand-logo" className="group flex items-center">
            <img src="/logo-horizontal.png" alt="Fitygrity" className="h-8 sm:h-9 w-auto object-contain rounded-md transition-opacity duration-300 group-hover:opacity-80" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <SignupLink
              testId="header-link-signup"
              className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] whitespace-nowrap text-[#FF4E00] hover:text-[#E04000] transition-colors duration-300"
            />
            <AccountMenu />
            <BackLink testId="gallery-back-link" />
          </div>
        </nav>
      </header>

      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16 sm:mb-24"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <ShoppingBag className="w-4 h-4" /> Athlete-Tested Equipment
          </p>
          <h1
            data-testid="gallery-headline"
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]"
          >
            The Gear <span className="italic text-[#FF4E00]">Room</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            Kit our coaches actually train and race in. Every pick links out to a trusted seller —
            no carts, no checkout, just the shortlist.
          </p>
        </motion.div>

        {collections === null ? (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6B7280]">Loading the shelves...</p>
        ) : collections.length === 0 ? (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6B7280]">
            The shelves are being stocked — check back soon.
          </p>
        ) : (
          <div className="space-y-20 sm:space-y-28">
            {collections.map((c) => (
              <section key={c.id} data-testid="gallery-collection">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className="mb-10"
                >
                  <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#0D1117] tracking-tight">
                    {c.name}
                  </h2>
                  {c.description && (
                    <p className="mt-3 text-sm sm:text-base text-[#374151] max-w-xl leading-relaxed">
                      {c.description}
                    </p>
                  )}
                </motion.div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                  {c.products.map((p, i) => (
                    <motion.a
                      key={p.id}
                      href={p.affiliate_url}
                      target="_blank"
                      rel="noopener sponsored"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: "easeOut" }}
                      data-testid="gallery-product"
                      className="group rounded-2xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] overflow-hidden transition-colors duration-500"
                    >
                      {p.image_url && (
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={p.image_url}
                            alt={p.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-serif text-xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors duration-300">
                            {p.name}
                          </h3>
                          {p.price_text && (
                            <span className="font-mono text-xs text-[#FF4E00] shrink-0 pt-1">{p.price_text}</span>
                          )}
                        </div>
                        {p.description && (
                          <p className="text-sm text-[#374151] leading-relaxed mt-3">{p.description}</p>
                        )}
                        <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] group-hover:text-[#FF4E00] transition-colors duration-300">
                          View Deal
                          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-45" />
                        </span>
                      </div>
                    </motion.a>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <p className="mt-20 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] max-w-2xl leading-relaxed">
          Prices are indicative street prices surveyed September 2026 across official India stores —
          they move with sales, so tap through for today's price. Outbound links are affiliate links;
          Fitygrity may earn a commission at no extra cost to you — it keeps the leaderboard lights on.
        </p>
      </main>

      <Footer />
    </div>
  );
}
